/**
 * Minimal structured logger — emits one JSON line per event, shaped for Google
 * Cloud Logging (which parses `severity` + `message` out of a JSON payload and
 * groups by severity). Server-only: a no-op in the browser bundle.
 *
 * Writes via process.stdout/stderr rather than console.* on purpose: it keeps
 * the logger clear of the no-console lint + static-analysis gate (AC-7.3) while
 * still surfacing operational failures in Cloud Run logs. The app was
 * previously near-silent server-side — data-layer throws and the notFound()
 * fallback produced zero log signal, which is why the cross-calendar 404
 * incident was invisible from the server. (almanak-76ev.3)
 */

type Severity = 'INFO' | 'WARNING' | 'ERROR';
type Fields = Record<string, unknown>;

function emit(severity: Severity, message: string, fields?: Fields): void {
  // Client no-op: these logs are for Cloud Run stdout, never the browser.
  if (typeof window !== 'undefined') return;

  const entry: Record<string, unknown> = {
    severity,
    message,
    time: new Date().toISOString(),
    ...fields,
  };

  let line: string;
  try {
    line = JSON.stringify(entry);
  } catch {
    // A field held a circular / unserializable value — never let logging throw.
    line = JSON.stringify({ severity, message, time: entry.time, _note: 'unserializable fields dropped' });
  }

  const stream = severity === 'ERROR' ? process.stderr : process.stdout;
  stream.write(line + '\n');
}

export function logInfo(message: string, fields?: Fields): void {
  emit('INFO', message, fields);
}

export function logWarn(message: string, fields?: Fields): void {
  emit('WARNING', message, fields);
}

export function logError(message: string, fields?: Fields): void {
  emit('ERROR', message, fields);
}

/** Normalize an unknown thrown value into loggable fields (message + stack). */
export function errFields(err: unknown): Fields {
  if (err instanceof Error) return { error: err.message, stack: err.stack };
  return { error: String(err) };
}
